import { isAxiosError } from "axios";
import bodycamClient from "./clients/bodycamClient";
import {
  LoadedTranscript,
  SaveTranscriptRequest,
  TranscriptData,
  TranscriptListResponse,
  TranscriptVersionSummary,
  TranscriptVersionsResponse,
  UpdateTranscriptResponse,
} from "../interfaces/Transcript";

/** Interval and overall budget for polling `GET /transcript` after a job is enqueued. */
const TRANSCRIPTION_POLL_INTERVAL_MS = 4000;
const TRANSCRIPTION_POLL_TIMEOUT_MS = 5 * 60_000;

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

type PollOutcome =
  | { state: "ready"; transcript: TranscriptData }
  | { state: "pending" } // 202 → job underway (evidence TRANSCRIBING)
  | { state: "absent" }; // 404 → no transcript and no job running

/**
 * Polls `GET /transcript` once. The endpoint has three meaningful outcomes:
 * `200` (ready), `202` (in progress, keep polling), and `404` (no transcript
 * and no job running — failed/reverted or never submitted). Other statuses
 * propagate as errors carrying the server message.
 */
const pollTranscript = async (
  videoId: string,
  signal?: AbortSignal,
): Promise<PollOutcome> => {
  try {
    const response = await bodycamClient.get<{ transcript?: TranscriptData }>(
      "/transcript",
      { params: { evidenceId: videoId, format: "json" }, signal },
    );
    // 202 (in progress) is a 2xx, so it resolves here rather than throwing.
    if (response.status === 202 || !response.data?.transcript) {
      return { state: "pending" };
    }
    return { state: "ready", transcript: response.data.transcript };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return { state: "absent" };
    }
    throw error;
  }
};

/**
 * Submits an async transcription job and resolves once the transcript is ready.
 *
 * `POST /transcribe` returns immediately: `200` with the transcript inline when
 * evidence was already transcribed, `202` when a job is enqueued, or `409` when
 * one is already running. For `202`/`409` we poll `GET /transcript` until it
 * returns `200`. Polling semantics: `202` = keep waiting, `404` = terminal (job
 * failed and reverted, or is not running). Because a freshly-`202`-queued job
 * briefly leaves the evidence `READY` (so `GET` `404`s before the worker starts),
 * a `404` is only treated as failure once we've observed the job actually running.
 * Other statuses (`400`/`403`) propagate as errors carrying the server message.
 *
 * Deliberately KEPT as an imperative promise when the Transcripts hooks moved
 * to react-query, rather than becoming a `refetchInterval` polling hook (the
 * jobStatusQuery / jobStatusPollInterval pattern in
 * features/jobs/api/jobQueries.ts). Its only consumer is useTranscription
 * (Generate Report), which transcribes N videos in PARALLEL via
 * `Promise.allSettled(videos.map((v) => transcribeVideo(v.id, signal)))`
 * under one AbortController and needs a single resolves-when-done value per
 * video — a polling hook can't be instantiated per item of a dynamic list
 * without breaking the rules of hooks. It is also mutation-shaped (submit a
 * job, await its terminal state), not a cacheable read: parking intermediate
 * CJI transcript snapshots in the shared query cache for a flow that
 * immediately navigates away would hurt cache hygiene, not help it. If a
 * single-video observable version is ever needed, build it on the jobQueries
 * pattern instead of reusing this loop.
 */
export const transcribeVideo = async (
  videoId: string,
  signal?: AbortSignal,
): Promise<TranscriptData> => {
  // Whether we've confirmed a job is/was running — a 409 submit or any 202 poll.
  // Until then, a 404 is the QUEUED-but-not-started window, not a failure.
  let jobRunning = false;
  try {
    const submit = await bodycamClient.post<{ transcript?: TranscriptData }>(
      "/transcribe",
      { videoId },
      { signal },
    );
    // 200 → already transcribed, transcript returned inline; done.
    if (submit.status === 200 && submit.data?.transcript) {
      return submit.data.transcript;
    }
    // 202 → job enqueued; fall through to polling.
  } catch (error) {
    // 409 → a job is already running; poll for its result rather than re-POSTing.
    if (isAxiosError(error) && error.response?.status === 409) {
      jobRunning = true;
    } else {
      throw error;
    }
  }

  const deadline = Date.now() + TRANSCRIPTION_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await sleep(TRANSCRIPTION_POLL_INTERVAL_MS, signal);
    const outcome = await pollTranscript(videoId, signal);
    if (outcome.state === "ready") return outcome.transcript;
    if (outcome.state === "pending") {
      jobRunning = true;
      continue;
    }
    // outcome.state === "absent" (404)
    if (jobRunning) {
      throw new Error("Transcription failed. Please try again.");
    }
    // Job hasn't been observed running yet (still QUEUED); keep waiting.
  }
  throw new Error("Transcription timed out. Please try again later.");
};

export const listTranscripts = async (
  params: { limit?: number; nextToken?: string; search?: string },
  signal?: AbortSignal,
): Promise<TranscriptListResponse> => {
  const response = await bodycamClient.get<TranscriptListResponse>(
    "/transcripts",
    { params, signal },
  );
  return response.data;
};

export const getTranscript = async (
  evidenceId: string,
  signal?: AbortSignal,
): Promise<LoadedTranscript> => {
  const response = await bodycamClient.get<{
    transcript: TranscriptData;
    versionNumber?: number;
  }>(`/transcripts/${encodeURIComponent(evidenceId)}`, {
    params: { format: "json" },
    signal,
  });
  return {
    transcript: response.data.transcript,
    // -1 means "version unknown" → save skips the optimistic-concurrency check.
    versionNumber: response.data.versionNumber ?? -1,
  };
};

/** Lists the saved revisions of a transcript, most recent first is not guaranteed (client sorts). */
export const getTranscriptVersions = async (
  evidenceId: string,
  signal?: AbortSignal,
): Promise<TranscriptVersionSummary[]> => {
  const response = await bodycamClient.get<TranscriptVersionsResponse>(
    `/transcripts/${encodeURIComponent(evidenceId)}/versions`,
    { signal },
  );
  return response.data.versions ?? [];
};

/** Loads a specific historical version of a transcript document. */
export const getTranscriptVersion = async (
  evidenceId: string,
  versionNumber: number,
  signal?: AbortSignal,
): Promise<LoadedTranscript> => {
  const response = await bodycamClient.get<{
    transcript: TranscriptData;
    versionNumber?: number;
  }>(`/transcripts/${encodeURIComponent(evidenceId)}`, {
    params: { version: versionNumber, format: "json" },
    signal,
  });
  return {
    transcript: response.data.transcript,
    versionNumber: response.data.versionNumber ?? versionNumber,
  };
};

export const getTranscriptThumbnail = async (
  evidenceId: string,
  signal?: AbortSignal,
): Promise<string> => {
  const response = await bodycamClient.get<{ url: string }>("/thumbnail", {
    params: { video_id: evidenceId },
    signal,
  });
  return response.data.url;
};

/**
 * Persists the entire edited transcript document (full-document replace), rather
 * than JSON-Patch deltas — robust to added/deleted/reordered entries. The server
 * always appends a new version continuing from the current head.
 */
export const saveTranscript = async (
  evidenceId: string,
  body: SaveTranscriptRequest,
): Promise<UpdateTranscriptResponse> => {
  const response = await bodycamClient.put<UpdateTranscriptResponse>(
    `/transcripts/${encodeURIComponent(evidenceId)}`,
    body,
  );
  return response.data;
};
